import React, { useState } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import TopUpModal from './TopUpModal';
import logger from '../utils/logger';

const PlansModal = ({ isOpen, onClose, currentTier = 'free', currentUsage = {} }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  const plans = [
    {
      tier: 'free',
      name: 'Free',
      price: '$0',
      period: '/month',
      images: 10,
      features: [
        '10 images per month',
        'Standard caption quality',
        '5 images per batch',
        'Community support'
      ],
      disabled: currentTier === 'free'
    },
    {
      tier: 'starter',
      name: 'Starter',
      price: '$6.99',
      period: '/month',
      images: 600,
      features: [
        '600 images per month',
        'Standard quality captions',
        '20 images per batch',
        'Unused credits roll over',
        'Email support',
        '20% savings on top-ups'
      ],
      disabled: currentTier === 'starter'
    },
    {
      tier: 'professional',
      name: 'Professional',
      price: '$10.99',
      period: '/month',
      images: 1200,
      features: [
        '1,200 images per month',
        'Detailed caption quality',
        '50 images per batch',
        'Unused credits roll over',
        'Custom tags',
        '30% savings on top-ups'
      ],
      recommended: currentTier === 'free',
      disabled: currentTier === 'professional'
    },
    {
      tier: 'business',
      name: 'Business',
      price: '$24.99',
      period: '/month',
      images: 3000,
      features: [
        '3,000 images per month',
        'All quality levels',
        '100 images per batch',
        'Unused credits roll over',
        'Bulk processing',
        'Priority support',
        '40% savings on top-ups'
      ],
      recommended: currentTier === 'professional',
      disabled: currentTier === 'business'
    },
    {
      tier: 'enterprise',
      name: 'Enterprise',
      price: '$69.99',
      period: '/month',
      images: 10000,
      features: [
        '10,000 images per month',
        'Unlimited quality options',
        '100+ images per batch',
        'Unused credits roll over',
        'Custom integrations',
        'Dedicated support',
        'Volume pricing available'
      ],
      recommended: currentTier === 'business',
      disabled: currentTier === 'enterprise'
    }
  ];

  const handleSubscribe = async (tier) => {
    if (tier === 'free' || tier === currentTier) return;
    
    setLoading(true);
    setSelectedTier(tier);
    
    try {
      const response = await api.billing.createCheckout(tier);
      
      if (response.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      logger.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout process. Please try again.');
      setLoading(false);
      setSelectedTier(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-surface border-b border-border p-4 sm:p-6 flex-shrink-0">
          <div className="flex items-start sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-primaryText">Choose Your Plan</h2>
              <p className="text-sm sm:text-base text-secondaryText mt-1">
                Upgrade to process more images and unlock advanced features
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-button rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-secondaryText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {plans.map((plan) => (
              <div
                key={plan.tier}
                className={`relative rounded-xl border-2 p-6 transition-all ${
                  plan.recommended
                    ? 'border-accent shadow-lg shadow-accent/20 scale-105'
                    : 'border-border hover:border-accent/50'
                } ${plan.disabled ? 'opacity-60' : ''}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-accent text-black px-3 py-1 rounded-full text-xs font-semibold">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                
                {plan.disabled && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-accent/20 text-accent px-3 py-1 rounded-full text-xs">
                      CURRENT PLAN
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-primaryText mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-3xl font-bold text-primaryText">{plan.price}</span>
                    <span className="text-secondaryText ml-1">{plan.period}</span>
                  </div>
                  <p className="text-accent font-medium mt-2">{plan.images.toLocaleString()} images</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-4 h-4 text-accent mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-secondaryText">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.tier)}
                  disabled={plan.disabled || loading}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${
                    plan.disabled
                      ? 'bg-button text-secondaryText cursor-not-allowed'
                      : plan.recommended
                      ? 'bg-accent text-black hover:bg-accent/90'
                      : 'bg-button text-primaryText hover:bg-accent hover:text-black'
                  } ${loading && selectedTier === plan.tier ? 'opacity-50' : ''}`}
                >
                  {loading && selectedTier === plan.tier ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </span>
                  ) : plan.disabled ? (
                    plan.tier === currentTier ? 'Current Plan' : 'Downgrade'
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-xl border-2 border-border hover:border-accent/50 transition-all">
            <h3 className="text-lg font-semibold text-primaryText mb-4">Need More Images?</h3>
            <p className="text-secondaryText mb-4">
              Purchase additional credits at discounted rates when you need them. All unused credits roll over to the next month as long as you maintain an active subscription. Never lose the credits you've paid for!
            </p>
            <button
              onClick={() => setShowTopUpModal(true)}
              className="bg-accent text-black hover:bg-accent/90 transition-all rounded-lg py-2 px-6 font-medium"
            >
              Buy Additional Credits
            </button>
          </div>
        </div>
      </div>
      
      {/* TopUp Modal */}
      <TopUpModal 
        isOpen={showTopUpModal} 
        onClose={() => setShowTopUpModal(false)}
        currentTier={currentTier}
        currentUsage={currentUsage}
      />
    </div>
  );
};

export default PlansModal;